import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ExternalLink, MessageCircle, Share2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { BlogLayout, useHead } from "../components/blog/BlogLayout";
import { RelatedPosts } from "../components/blog/RelatedPosts";
import { NewsletterSignup } from "../components/blog/Newsletter";
import { BlogBlocks } from "../components/blog/BlogBlocks";
import { API, mediaUrl, fmtDate, HTML_LANG } from "../lib/blogApi";

export default function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const counted = useRef(null);

  useEffect(() => {
    setP(null); setErr(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/blogdata/posts/${slug}`).then(({ data }) => setP(data)).catch(() => setErr(true));
  }, [slug]);

  useEffect(() => {
    if (!p || counted.current === p.slug) return;
    counted.current = p.slug;
    axios.post(`${API}/blogdata/posts/${p.slug}/view`).catch(() => {});
  }, [p]);

  useHead({
    title: p ? `${p.title} — Nickolass Jensen` : "Blogg — Nickolass Jensen",
    description: p?.excerpt || "",
    canonical: p ? (p.source_url || `https://nickolass.com/blog/${p.slug}`) : null,
    alternate: `${API}/blog/${slug}`,
    lang: p?.lang || "no",
  });

  const no = p?.lang !== "en";
  const share = async () => {
    const url = `https://nickolass.com/blog/${p.slug}`;
    try {
      if (navigator.share) await navigator.share({ title: p.title, url });
      else { await navigator.clipboard.writeText(url); toast.success(no ? "Lenke kopiert" : "Link copied"); }
    } catch { /* cancelled */ }
  };

  if (err) return (
    <BlogLayout><div className="mx-auto max-w-[720px] px-5 py-24"><h1 className="font-serif text-4xl">Fant ikke innlegget</h1>
      <p className="mt-4"><Link to="/blog" className="underline underline-offset-4">Tilbake til bloggen</Link></p></div></BlogLayout>
  );
  if (!p) return <BlogLayout><p className="mx-auto max-w-[720px] px-5 py-24" role="status">Laster…</p></BlogLayout>;

  const cover = p.cover_media_id ? mediaUrl(p.cover_media_id) : p.cover_url;
  const external = p.source !== "own";
  return (
    <BlogLayout>
      <article lang={HTML_LANG[p.lang]} data-testid="blog-article" className="mx-auto max-w-[720px] px-5 md:px-0 pt-14 md:pt-20">
        <p><Link to="/blog" data-testid="blog-back" className="font-mono text-[12px] tracking-[0.14em] uppercase underline underline-offset-4 hover:text-[#7a2d00]">← {no ? "Alle innlegg" : "All posts"}</Link></p>
        <header className="mt-8">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12px] tracking-[0.1em] uppercase text-[#4a4a4a]">
            <time dateTime={p.published_at}>{fmtDate(p.published_at, p.lang)}</time>
            {p.reading_min && <><span aria-hidden="true">·</span><span>{p.reading_min} min {no ? "lesetid" : "read"}</span></>}
            {external && <span className="px-2 py-0.5 border border-[#7a2d00] text-[#7a2d00]">{no ? "fra" : "from"} {p.source_name}</span>}
          </div>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">{p.title}</h1>
          {p.excerpt && !external && <p className="mt-6 text-xl leading-relaxed text-[#2a2a2a]">{p.excerpt}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            {p.tags?.length > 0 && <ul className="flex flex-wrap gap-3" aria-label={no ? "Tagger" : "Tags"}>
              {p.tags.map((t) => <li key={t}><Link to={`/blog?tag=${t}`} className="font-mono text-[13px] underline-offset-4 hover:underline">#{t}</Link></li>)}
            </ul>}
            <button type="button" onClick={share} data-testid="blog-share"
              className="inline-flex items-center gap-2 font-mono text-[12px] tracking-[0.12em] uppercase border border-ink px-3 py-1.5 hover:bg-ink hover:text-paper">
              <Share2 size={14} aria-hidden="true" />{no ? "Del" : "Share"}
            </button>
          </div>
        </header>
        {cover && <img src={cover} alt={p.cover_alt || ""} onError={(e) => { e.currentTarget.style.display = "none"; }} className="mt-10 w-full h-auto border border-ink" />}
        <div className="mt-8"><BlogBlocks blocks={p.blocks || []} lang={p.lang} /></div>
        {external && !(p.blocks || []).some((b) => (b.text || "").includes(p.source_url)) && (
          <a href={p.source_url} data-testid="blog-source-link" rel="noopener"
            className="mt-10 inline-flex items-center gap-3 px-6 py-3 bg-ink text-paper rounded-full font-mono text-[13px] tracking-[0.12em] uppercase hover:bg-ember">
            <ExternalLink size={16} aria-hidden="true" />{no ? "Les hele artikkelen på" : "Read the full article on"} {p.source_name}
          </a>
        )}
        <aside className="mt-16 border border-ink p-6 bg-sand" aria-label={no ? "Snakk med Nickolass" : "Talk to Nickolass"}>
          <p className="font-serif text-2xl">{no ? "Lurer du på noe om dette?" : "Questions about this?"}</p>
          <p className="mt-2">{no ? "Spør den digitale tvillingen min — eller book en prat med meg." : "Ask my digital twin — or book a call with me."}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <a href={`/?ask=${encodeURIComponent(no ? `Fortell meg mer om innlegget ditt «${p.title}» – hva er det viktigste jeg bør ta med meg?` : `Tell me more about your post “${p.title}” – what's the key takeaway?`)}#chat`}
              data-testid="blog-ask-twin"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-paper rounded-full font-mono text-[13px] tracking-[0.12em] uppercase hover:bg-ember transition-colors">
              <MessageCircle size={16} aria-hidden="true" />{no ? "Spør tvillingen om dette" : "Ask the twin about this"}
            </a>
            <a href="/#chat" data-testid="blog-cta-chat" className="underline underline-offset-4 font-mono text-[13px] uppercase tracking-[0.12em]">{no ? "Til chatten →" : "Open the chat →"}</a>
          </div>
        </aside>
        <RelatedPosts slug={p.slug} no={no} />
        <div className="mt-12"><NewsletterSignup defaultLang={p.lang} compact /></div>
      </article>
      <Toaster position="top-center" />
    </BlogLayout>
  );
}
